/**
 * Keeps the product spec table in step with the selected variant.
 *
 * The table lives in a product accordion row, a child of the `_product-media` block,
 * which `sections/main-product.liquid` skips in the `product.hdt_ajax` view — so the
 * markup Athora fetches after a variant change does not contain it. Unlike the
 * assembly instructions (`instructions-link.js`), the rows are not shipped as a map
 * for every variant: a bed has up to 160 variants with ~30 rows each, and the number
 * formatting would have to be duplicated here. Instead the rows of the new variant are
 * fetched through the Section Rendering API from `sections/variant-specs.liquid`, so
 * `snippets/variant-specs.liquid` stays the only place that decides what a row says.
 *
 * Responses are cached per URL: stepping back to a variant already seen costs nothing.
 * A newer variant change aborts the request still in flight, so a slow response can
 * never overwrite the table of a variant picked after it.
 *
 * Whenever the rows of the selected variant cannot be shown — a failed request, or an
 * option combination that matches no variant — the row is hidden instead of keeping the
 * previous variant's table: a bed listed as 140 cm wide while 180 cm is selected is
 * worse than no table at all.
 *
 * Loaded as a module from `blocks/_product-accordion-item.liquid`; module scripts with
 * the same URL are evaluated once per document.
 *
 * Documented in `materials/altdecor-docs/product-accordion.md`.
 */

const ROW_SELECTOR = '[data-specs-row]';
const BODY_SELECTOR = '[data-specs-body]';
const SECTION_ID = 'variant-specs';

const cache = new Map();
let controller = null;

const specsUrl = (row, variantId) => {
  const url = new URL(row.dataset.specsUrl, window.location.origin);
  url.searchParams.set('variant', String(variantId));
  url.searchParams.set('section_id', SECTION_ID);
  return url.toString();
};

const fetchRows = async (url, signal) => {
  if (cache.has(url)) return cache.get(url);

  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`variant-specs: HTTP ${response.status}`);

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const body = doc.querySelector(BODY_SELECTOR);
  if (!body) throw new Error('variant-specs: no rows in the response');

  const rows = body.innerHTML.trim();
  cache.set(url, rows);
  return rows;
};

const renderRows = async (rows, variantId) => {
  controller?.abort();
  controller = new AbortController();
  const { signal } = controller;

  for (const row of rows) {
    if (row.dataset.variantId === String(variantId)) continue;
    const body = row.querySelector(BODY_SELECTOR);
    if (!body) continue;

    try {
      const html = await fetchRows(specsUrl(row, variantId), signal);
      if (signal.aborted) return;
      body.innerHTML = html;
      row.dataset.variantId = String(variantId);
      row.hidden = html === '';
    } catch (error) {
      if (error.name === 'AbortError') return;
      hideRow(row);
      console.warn(error);
    }
  }
};

const hideRow = (row) => {
  row.hidden = true;
  // Forget the variant, so selecting it again retries instead of being skipped.
  delete row.dataset.variantId;
};

const syncSpecs = (event) => {
  // `variant-change` bubbles to the document from every product form, including the
  // quick view of another product. Only rows in the same section as the form follow it.
  const rows = [...document.querySelectorAll(ROW_SELECTOR)].filter((row) =>
    row.closest('.shopify-section')?.contains(event.target),
  );
  if (!rows.length) return;

  const variantId = event.detail?.variant?.id;
  if (variantId) {
    renderRows(rows, variantId);
    return;
  }

  // No variant for the picked option combination: nothing in the table is true any more.
  controller?.abort();
  for (const row of rows) hideRow(row);
};

document.addEventListener('variant-change', syncSpecs);
